import { AddCircle } from '@mui/icons-material';
import { Button, ButtonBase, Grid, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import {
  Attachment,
  MIXPANEL_EVENTS,
  PermissionsEnum,
  WorkOrder,
} from 'vendor-marketplace-shared-library';

import workOrderService from '../../../services/work-order.service';
import Modal from '../../../components/common/Modal';
import ImageThumbnail from '../../../components/common/ImageThumbnail';
import useAuth from '../../../hooks/useAuth';
import useMixpanel from '../../../hooks/useMixpanel';

type WorkOrderAttachmentsModuleProps = {
  attachments: Attachment[];
  workOrder: WorkOrder;
  refetchWorkOrders: () => void;
};

export default function WorkOrderAttachmentsModule(props: WorkOrderAttachmentsModuleProps) {
  const { validateWorkOrderUserPermissions } = useAuth();
  const { mixpanelTrack } = useMixpanel();
  const inputRef = useRef<HTMLInputElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [attachmentId, setAttachmentId] = useState('');

  const onUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await onUploadAttachments(file);
  };

  const onUploadAttachments = async (file: File) => {
    await workOrderService.uploadAndAddAttachmentsToWorkOrder(Number(props.workOrder.id), file);
    props.refetchWorkOrders();
  };

  const onRemoveAttachment = async (attachmentId: string) => {
    await workOrderService.removeAttachmentsFromWorkOrder(Number(props.workOrder.id), [
      Number(attachmentId),
    ]);
    props.refetchWorkOrders();
    mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_DELETE_ATTACHMENT_COMPLETED);
  };

  return (
    <Grid container direction={'column'} rowGap={2}>
      <Typography variant={'h6'}>Files</Typography>
      <Grid item sx={styles.subContainer}>
        <Grid container columnGap={2} sx={styles.imageContainer}>
          <UploadFilesThumbnail
            onClick={() => {
              mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_DETAILS_CREATE_ATTACHMENT_PRESSED);
              onUploadClick();
            }}
          />
          <input hidden type={'file'} ref={inputRef} onChange={onChange} />
          {props.attachments.map(attachment => (
            <ImageThumbnail
              key={attachment.id}
              attachment={attachment}
              onDelete={() => {
                setAttachmentId(attachment.id!);
                setModalOpen(true);
              }}
              deleteDisabled={
                !validateWorkOrderUserPermissions(
                  props.workOrder,
                  PermissionsEnum.WORK_ORDER_DETAILS_UPDATE,
                )
              }
            />
          ))}
        </Grid>
      </Grid>
      {modalOpen && (
        <Modal title={'Delete Attachment Confirmation'} open={modalOpen} setOpen={setModalOpen}>
          <Grid container xs={12} sx={{ padding: 1, alignItems: 'center' }} rowGap={2}>
            <Grid item xs={12}>
              <Typography>Are you sure you want to delete this attachment?</Typography>
            </Grid>
            <Grid item xs={4}>
              <Button
                variant={'contained'}
                fullWidth
                color={'error'}
                onClick={() => {
                  mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_UPDATE_DELETE_ATTACHMENT_ATTEMPTED);
                  onRemoveAttachment(attachmentId);
                  setModalOpen(false);
                }}
              >
                Delete
              </Button>
            </Grid>
          </Grid>
        </Modal>
      )}
    </Grid>
  );
}

function UploadFilesThumbnail(props: { onClick: () => void }) {
  return (
    <ButtonBase>
      <Grid
        container
        direction={'column'}
        rowGap={1}
        onClick={props.onClick}
        sx={styles.uploadContainer}
      >
        <AddCircle />
        <Typography variant={'body2'}>Upload file(s)</Typography>
      </Grid>
    </ButtonBase>
  );
}

const styles = {
  imageContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingTop: '10px',
    wrap: 'nowrap',
    direction: 'row',
    justifyContent: 'flex-start',
    flex: 1,
  },
  subContainer: {
    backgroundColor: 'secondary.light',
    padding: 2,
    borderRadius: 2,
    boxShadow: 2,
    minHeight: 150,
  },
  uploadContainer: {
    padding: 1,
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    borderRadius: 2,
    maxWidth: 100,
    backgroundColor: 'common.white',
    border: '2px solid',
    borderColor: 'primary.main',
    transition: '0.5s',
    '&:hover': {
      backgroundColor: 'primary.main',
      color: 'common.white',
    },
  },
};
